/**
 * @fileoverview Copy/Recurrence API Hooks
 * @author Hospital Room Scheduler Team
 * @created 2025-01-08
 * @updated 15-10-2025 11:00:00 - Normalized API response unwrapping for data binding fix
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

/**
 * Available dates API response structure
 * Contains array of dates with assignment metadata
 */
interface AvailableDatesResponse {
  availableDates: Array<{
    date: string;
    assignmentCount: number;
    assignments: Array<{
      roomId: string;
      roomName: string;
      roomNumber: string;
      sessionId: string;
      sessionName: string;
      doctorName: string;
      userId: string;
    }>;
    isToday: boolean;
    isPast: boolean;
    roomCount: number;
    sessionCount: number;
    doctorCount: number;
  }>;
  totalDates: number;
  totalAssignments: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Preview/Copy operation response structure
 * Contains conflict detection and operation summary
 */
interface PreviewCopyResponse {
  preview: boolean;
  copiedCount: number;
  potentialCopies: number;
  sourceAssignments: number;
  targetDates: number;
  conflicts: Array<{
    date: string;
    roomId: string;
    roomNumber: string;
    sessionName: string;
    doctorName: string;
    conflicts: Array<{
      code: string;
      message: string;
      suggestion?: any;
    }>;
  }>;
  skippedCount: number;
  totalAttempted: number;
  assignments: Array<any>;
}

/**
 * Hook to fetch available dates with assignments for copy operations
 * Returns unwrapped data payload (not envelope) for direct consumption
 * 
 * @param params - Query parameters for filtering available dates
 * @returns Available dates query result with availableDates array
 * @created 15-10-2025 14:50:00
 * @updated 15-10-2025 11:05:00 - Unwrap API envelope to return data.data
 */
export const useAvailableDates = (params: {
  roomIds?: string[];
  sessionTemplateIds?: string[];
  startDate?: string;
  endDate?: string;
  buildingId?: string;
  floorId?: string;
}) => {
  return useQuery<AvailableDatesResponse>({
    queryKey: ['available-dates', params],
    queryFn: async () => {
      const response = await api.get('/assignments/available-dates', { params });
      // API returns: { success: true, data: { availableDates: [...], ... } }
      // Return inner data payload only
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - dates change frequently
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: true, // Always enabled - load dates immediately when modal opens
  });
};

/**
 * Hook to copy assignments
 * Calls /api/v1/assignments/copy endpoint with conflict detection
 * Returns unwrapped data payload for direct consumption
 * 
 * @returns Copy assignments mutation
 * @updated 15-10-2025 12:30:00 - Updated endpoint to /assignments/copy
 * @updated 15-10-2025 11:07:00 - Unwrap API envelope to return data.data
 */
export const useCopyAssignments = () => {
  const queryClient = useQueryClient();

  return useMutation<PreviewCopyResponse, Error, any>({
    mutationFn: async (copyData: any) => {
      const response = await api.post('/assignments/copy', copyData);
      // API returns: { success: true, data: { copiedCount, conflicts, ... } }
      // Return inner data payload only
      return response.data.data;
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['planner-grid'] });
      queryClient.invalidateQueries({ queryKey: ['planner-filters'] });
      queryClient.invalidateQueries({ queryKey: ['room-detail'] });
    },
  });
};


/**
 * Hook to preview copy operation with dry-run mode
 * Validates conflicts without creating assignments
 * Returns unwrapped data payload for direct consumption
 * 
 * @returns Preview copy mutation
 * @updated 15-10-2025 12:30:00 - Updated to use dry-run parameter
 * @updated 15-10-2025 11:10:00 - Unwrap API envelope to return data.data
 */
export const usePreviewCopy = () => {
  return useMutation<PreviewCopyResponse, Error, any>({
    mutationFn: async (copyData: any) => {
      const response = await api.post('/assignments/copy', { ...copyData, dryRun: true });
      // API returns: { success: true, data: { potentialCopies, conflicts, ... } }
      // Return inner data payload only
      return response.data.data;
    },
  });
};

/**
 * Hook to preview recurrence operation without database writes
 * Shows generated dates and potential conflicts
 * @returns Preview recurrence mutation  
 * @updated 15-10-2025 12:30:00 - Updated to use dry-run approach
 */
export const usePreviewRecurrence = () => {
  return useMutation({
    mutationFn: async (recurrenceData: any) => {
      // Preview by generating dates client-side or using dry-run on backend
      const response = await api.post('/assignments/recurring', { ...recurrenceData, dryRun: true });
      return response.data;
    },
  });
};
